import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import ExportButtons from '../../../components/ExportButtons'
import SearchBar from '../../../components/SearchBar'
import { useMineMerker } from '../../../hooks/useMineMerker'
import type { ColumnDefinition, ExportMetadata } from '../../../utils/exportToCsv'
import type { MerkeRow } from './api'
import { formatAndel } from './api'

interface Props {
  rows: MerkeRow[]
  onRowClick?: (merke: string) => void
  /** Opens the merke's market-share page (#489 AC5). Kept as a callback rather
   *  than a router hook so this stays a presentational table. Omitted = no
   *  Detaljer column. */
  onDetaljer?: (merke: string) => void
  /** Period / active-filter context for the export; merged over the table's
   *  own default title. */
  exportMeta?: Partial<ExportMetadata>
  // Pagination + server-side search (#476) — all optional; when omitted the
  // table renders exactly as before (no search box, no footer, Rang = row
  // index + 1). The search box is server-side (calls back to the parent,
  // which re-fetches with `q=`) so it reaches merker beyond the current page
  // — unlike Kjøpere's client-side-only search of the current page.
  page?: number
  pages?: number
  total?: number
  onPageChange?: (page: number) => void
  search?: string
  onSearchChange?: (q: string) => void
  /** 1-based rank of rows[0] — the parent knows the real page size used for
   *  the fetch (rows.length on the last page is <= page size, so deriving
   *  this from page/rows.length here would be wrong). Defaults to 1. */
  startRank?: number
}

export default function MerkeTable({
  rows, onRowClick, onDetaljer, exportMeta,
  page, pages, total, onPageChange, search, onSearchChange, startRank = 1,
}: Props) {
  const { t } = useTranslation('bruktbil')
  const { activeMerker } = useMineMerker()

  const exportColumns: ColumnDefinition[] = [
    { key: 'rang', label: t('common.rang') },
    { key: 'merke', label: t('common.merke') },
    { key: 'count', label: t('common.antall'), format: (v: number) => v.toLocaleString('nb-NO') },
    { key: 'andel', label: t('common.markedsandel'), format: (v: number) => formatAndel(v) },
  ]

  // The export rows mirror the rendered table, including the Rang column.
  const exportRows = rows.map((row, i) => ({ rang: startRank + i, ...row }))

  return (
    <div data-testid="merke-table" className="card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('merkeModell.merkeTable.title')}</span>
        <ExportButtons
          data={exportRows}
          columns={exportColumns}
          metadata={{ title: t('merkeModell.merkeTable.exportTitle'), ...exportMeta }}
        />
      </div>
      {onSearchChange && (
        <div className="px-4 py-2 border-b border-gray-200 dark:border-dark-border">
          <SearchBar value={search ?? ''} onChange={onSearchChange} placeholder={t('merkeModell.merkeTable.sok')} />
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-card">
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.rang')}</th>
            <th className="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('common.merke')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
            <th className="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.markedsandel')}</th>
            {onDetaljer && <th className="px-4 py-2 text-center font-medium text-gray-600 dark:text-gray-400"></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.merke ?? i}
              onClick={() => row.merke && onRowClick?.(row.merke)}
              className={`border-b border-gray-100 dark:border-dark-border ${
                onRowClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-border' : ''
              } ${row.merke && activeMerker.has(row.merke) ? 'bg-primary-100 dark:bg-primary-900/30 border-l-4 border-primary-500' : ''}`}
            >
              <td className="px-4 py-2 text-gray-500 tabular-nums">{startRank + i}</td>
              <td className="px-4 py-2">{row.merke ?? '—'}</td>
              <td className="px-4 py-2 text-right tabular-nums">{row.count.toLocaleString('nb-NO')}</td>
              <td className="px-4 py-2 text-right tabular-nums">{formatAndel(row.andel)}</td>
              {/* The row itself drills into this merke's models (#489 AC2);
               *  this goes to the market-share page (AC5). Two destinations,
               *  so the button must not inherit the row's click. */}
              {onDetaljer && <td className="px-4 py-2 text-center">
                {row.merke && (
                  <button
                    type="button"
                    data-testid={`merke-detaljer-${row.merke}`}
                    onClick={e => { e.stopPropagation(); onDetaljer(row.merke!) }}
                    className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <span className="text-xs font-medium">{t('common.seDetaljer')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </td>}
            </tr>
          ))}
        </tbody>
      </table>

      {rows.length === 0 && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('common.ingenTreff')}</p>
      )}

      {onPageChange && (pages ?? 1) > 1 && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 px-4 py-3 border-t border-gray-200 dark:border-dark-border">
          <span>
            {t('pagination.sideAvTotalt', { page, pages, total: (total ?? 0).toLocaleString('nb-NO') })}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(Math.max(1, (page ?? 1) - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('pagination.forrige')}
            </button>
            <button
              onClick={() => onPageChange(Math.min(pages ?? 1, (page ?? 1) + 1))}
              disabled={page === pages}
              className="px-3 py-1 rounded-sm border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('pagination.neste')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
